/**
 * Geography helpers of the 3D view that need no three.js, so the offline saver (in the main bundle)
 * can use them without pulling the 3D chunk in.
 */
import type { LatLon, TerrainMeta } from "@divespots/contracts";
import { wgs84ToLv95 } from "@divespots/contracts";
import { wmsBase } from "../../map/endpoints.ts";

/** Outer edges of the tile in LV95: NW cell centre +- half a cell, `width` x `height` cells of `cellSizeM`. */
export function aerialBbox(meta: TerrainMeta) {
  const minE = meta.originLv95.e - meta.cellSizeM / 2;
  const maxN = meta.originLv95.n + meta.cellSizeM / 2;
  return {
    minE,
    maxE: minE + meta.width * meta.cellSizeM,
    maxN,
    minN: maxN - meta.height * meta.cellSizeM,
  };
}

export type Bbox = ReturnType<typeof aerialBbox>;

/** 2048 px for tiles of 150 m radius and more (the WMS serves up to 4096), 1024 for the small ones. */
export const aerialSize = (meta: Pick<TerrainMeta, "radiusM">): number =>
  meta.radiusM >= 150 ? 2048 : 1024;

/** True when `inner` lies inside `outer` (so a photo of `outer` can be reused for `inner`). */
export const bboxCovers = (outer: Bbox, inner: Bbox, tol = 0.01): boolean =>
  inner.minE >= outer.minE - tol &&
  inner.maxE <= outer.maxE + tol &&
  inner.minN >= outer.minN - tol &&
  inner.maxN <= outer.maxN + tol;

/**
 * The part of a photo of `outer` that shows `inner`, as a UV transform `uv * scale + offset` for a
 * texture whose v = 1 is the north edge: [scaleU, scaleV, offsetU, offsetV].
 */
export function aerialSubRect(outer: Bbox, inner: Bbox): [number, number, number, number] {
  const ow = outer.maxE - outer.minE;
  const oh = outer.maxN - outer.minN;
  return [
    (inner.maxE - inner.minE) / ow,
    (inner.maxN - inner.minN) / oh,
    (inner.minE - outer.minE) / ow,
    (inner.minN - outer.minN) / oh,
  ];
}

/**
 * swissimage for the tile, straight from the swisstopo WMS (CORS-enabled). For EPSG:2056 this
 * server wants BBOX in E,N order even with WMS 1.3.0 (checked: N,E returns a blank image).
 */
export function aerialUrl(meta: TerrainMeta, size = aerialSize(meta)): string {
  const b = aerialBbox(meta);
  const bbox = [b.minE, b.minN, b.maxE, b.maxN].map((v) => v.toFixed(2)).join(",");
  return (
    `${wmsBase()}/?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap` +
    `&LAYERS=ch.swisstopo.swissimage&STYLES=&CRS=EPSG:2056&BBOX=${bbox}` +
    `&WIDTH=${size}&HEIGHT=${size}&FORMAT=image/jpeg`
  );
}

/**
 * The tile radius to open with: the smallest preset (default 150 m minimum) whose square tile
 * contains every object around the entry point; the largest preset when none does.
 */
export function initialRadius(
  entry: LatLon,
  objects: readonly LatLon[],
  presets: readonly number[] = [150, 300],
): number {
  const c = wgs84ToLv95(entry);
  const reach = objects.reduce((m, o) => {
    const p = wgs84ToLv95(o);
    return Math.max(m, Math.abs(p.e - c.e), Math.abs(p.n - c.n));
  }, 0);
  return presets.find((r) => reach <= r - 1) ?? (presets[presets.length - 1] as number);
}
